/**
 * Sweep Blast Radius diagnostic card (read-only).
 *
 * Incident: the Stale Suggested Matches Sweep's Apply superseded 3124 of
 * 3518 scanned matches in prod against a preview-time expectation of ~702
 * suggested / 622 kills, with invoice_missing / transaction_missing reason
 * buckets that should be impossible for a genuine invoice<->transaction
 * pair. This card reconstructs the blast radius against current data:
 * census by kind and recomputed reason bucket, a structural 0-check that a
 * currently-confirmed match can never carry the sweep's marker, samples of
 * matches that show a prior-confirm-then-reverted residue, orphaned
 * reconciliation_memory rows, and a cross-collection contamination check
 * against statement_payout_matches / recurring_cost_matches (which the
 * sweep can never touch by construction -- verified here, not assumed).
 * GET only, never writes, never restores.
 */

import { useState } from 'react';
import { Loader2, Radar, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { sweepBlastRadiusAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatDate(isoStr) {
  if (!isoStr) return '--';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

function StructuralCheckRow({ label, count, note }) {
  var ok = count === 0;
  return (
    <div className="flex items-start gap-2 text-xs border border-border rounded p-2">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
      )}
      <div>
        <p>
          <span className="font-medium">{label}:</span>{' '}
          <span className={ok ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-red-700 dark:text-red-400 font-bold'}>
            {count}
          </span>
        </p>
        <p className="text-muted-foreground mt-0.5">{note}</p>
      </div>
    </div>
  );
}

function KindCensusTable({ census }) {
  var byKind = census.by_kind;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{census.prior_status_note}</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-1.5 pr-3 font-medium">Kind</th>
            <th className="py-1.5 pr-3 font-medium">Count</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(byKind).map(function (kind) {
            var flagged = kind === 'anomalous_shape' && byKind[kind] > 0;
            return (
              <tr key={kind} className="border-b border-border/50">
                <td className="py-1.5 pr-3 font-mono text-xs">{kind}</td>
                <td className={'py-1.5 pr-3 text-xs' + (flagged ? ' text-red-700 dark:text-red-400 font-bold' : '')}>
                  {byKind[kind]}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {census.anomalous_shape_samples && census.anomalous_shape_samples.length > 0 && (
        <div className="text-xs border border-red-300 dark:border-red-900 rounded p-2 space-y-1">
          <p className="font-medium text-red-700 dark:text-red-400">Anomalous-shape samples:</p>
          {census.anomalous_shape_samples.map(function (s) {
            return (
              <p key={s.match_id} className="font-mono text-muted-foreground">
                {s.match_id}: [{s.fields_present.join(', ')}]
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReasonBucketTable({ buckets }) {
  var keys = Object.keys(buckets);
  if (keys.length === 0) {
    return <p className="text-xs text-muted-foreground">No superseded matches to bucket.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left">
          <th className="py-1.5 pr-3 font-medium">Reason (recomputed now)</th>
          <th className="py-1.5 pr-3 font-medium">Count</th>
          <th className="py-1.5 pr-3 font-medium">By kind</th>
        </tr>
      </thead>
      <tbody>
        {keys.map(function (reason) {
          var b = buckets[reason];
          return (
            <tr key={reason} className="border-b border-border/50">
              <td className="py-1.5 pr-3 font-mono text-xs">{reason}</td>
              <td className="py-1.5 pr-3 text-xs">{b.count}</td>
              <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                {Object.keys(b.by_kind).map(function (k) { return k + '=' + b.by_kind[k]; }).join(', ')}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ResidueSamplesTable({ samples }) {
  if (samples.length === 0) {
    return <p className="text-xs text-muted-foreground">None found.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-1.5 pr-3 font-medium">Match ID</th>
            <th className="py-1.5 pr-3 font-medium">Transaction</th>
            <th className="py-1.5 pr-3 font-medium">Invoice</th>
            <th className="py-1.5 pr-3 font-medium">Confirmed via</th>
            <th className="py-1.5 pr-3 font-medium">Memory confirm count</th>
            <th className="py-1.5 pr-3 font-medium">Tx reconciled now?</th>
            <th className="py-1.5 pr-3 font-medium">Tx matched_invoice_id now</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(function (s) {
            return (
              <tr key={s.match_id} className="border-b border-border/50">
                <td className="py-1.5 pr-3 font-mono text-xs">{s.match_id}</td>
                <td className="py-1.5 pr-3 font-mono text-xs">{s.transaction_id}</td>
                <td className="py-1.5 pr-3 font-mono text-xs">{s.invoice_id || '--'}</td>
                <td className="py-1.5 pr-3 text-xs">{s.confirm_provenance.confirmed_via}</td>
                <td className="py-1.5 pr-3 text-xs">{s.confirm_provenance.memory_confirm_count ?? '--'}</td>
                <td className="py-1.5 pr-3 text-xs">
                  {s.reconciliation_state_now.transaction_is_reconciled === null
                    ? 'tx not found'
                    : s.reconciliation_state_now.transaction_is_reconciled ? 'yes' : 'no'}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs">{s.reconciliation_state_now.transaction_matched_invoice_id || '--'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MemoryOrphanSamplesTable({ samples }) {
  if (samples.length === 0) {
    return <p className="text-xs text-muted-foreground">None found.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-1.5 pr-3 font-medium">Memory ID</th>
            <th className="py-1.5 pr-3 font-medium">Target kind</th>
            <th className="py-1.5 pr-3 font-medium">Target</th>
            <th className="py-1.5 pr-3 font-medium">Confirm count</th>
            <th className="py-1.5 pr-3 font-medium">Source match IDs</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(function (s) {
            return (
              <tr key={s.memory_id} className="border-b border-border/50">
                <td className="py-1.5 pr-3 font-mono text-xs">{s.memory_id}</td>
                <td className="py-1.5 pr-3 text-xs">{s.target_kind}</td>
                <td className="py-1.5 pr-3 text-xs">{s.target_id}</td>
                <td className="py-1.5 pr-3 text-xs">{s.confirm_count}</td>
                <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{(s.created_from_match_ids || []).join(', ')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ContaminationChecks({ checks }) {
  return (
    <div className="space-y-2">
      {Object.keys(checks).map(function (collName) {
        var c = checks[collName];
        return (
          <StructuralCheckRow
            key={collName}
            label={collName + ' docs carrying the sweep marker'}
            count={c.count_carrying_sweep_marker}
            note={'Isolated by design (' + c.code_reference + '). Non-zero here means the sweep touched a collection it structurally should never reach.'}
          />
        );
      })}
    </div>
  );
}

export function SweepBlastRadiusPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);

  var run = async function () {
    setLoading(true);
    try {
      var res = await sweepBlastRadiusAPI.run();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Radar className="h-4 w-4" />
          Sweep Blast Radius
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only. Censuses every db.matches doc carrying the Stale
          Suggested Matches Sweep's supersede marker (status=
          superseded_by_rerun, dismissal_reason=invalidated_by_matching_gates_sweep)
          by kind and recomputed reason bucket, checks the structural
          guarantee that a currently-confirmed match can never carry that
          marker, samples matches that show a prior-confirm-then-reverted
          residue, flags orphaned reconciliation_memory rows, and checks
          statement_payout_matches / recurring_cost_matches for
          contamination. Never writes, never restores.
        </p>

        <Button onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Radar className="h-4 w-4 mr-2" />}
          Run Census
        </Button>

        {data && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {data.superseded_scanned} of {data.superseded_total} superseded match{data.superseded_total !== 1 ? 'es' : ''} scanned at {formatDate(data.checked_at)}
                {data.truncated && (
                  <Badge className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]">
                    truncated -- re-run to see the remainder
                  </Badge>
                )}
              </span>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>Provenance filter: <span className="font-mono">{JSON.stringify(data.provenance.filter)}</span></p>
              <p>Code reference: <span className="font-mono">{data.provenance.code_reference}</span></p>
            </div>

            <section className="space-y-2">
              <h4 className="text-sm font-medium">Structural guarantees</h4>
              <StructuralCheckRow
                label="Currently-confirmed docs carrying the sweep marker"
                count={data.currently_confirmed_with_marker.count}
                note={data.currently_confirmed_with_marker.note}
              />
              <ContaminationChecks checks={data.cross_collection_contamination_check} />
            </section>

            <section className="space-y-2">
              <h4 className="text-sm font-medium">Census by kind</h4>
              <KindCensusTable census={data.census_by_kind_and_prior_status} />
            </section>

            <section className="space-y-2">
              <h4 className="text-sm font-medium">Reason bucket x kind</h4>
              <p className="text-xs text-muted-foreground">{data.reason_bucket_note}</p>
              <ReasonBucketTable buckets={data.reason_bucket_x_kind_recomputed} />
            </section>

            <section className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                Prior-confirm residue
                <Badge variant="outline" className="text-xs">{data.prior_confirm_residue.count}</Badge>
              </h4>
              <p className="text-xs text-muted-foreground">{data.prior_confirm_residue.note}</p>
              <ResidueSamplesTable samples={data.prior_confirm_residue.samples} />
            </section>

            <section className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                reconciliation_memory orphans
                <Badge variant="outline" className="text-xs">{data.reconciliation_memory_orphans.count}</Badge>
              </h4>
              <p className="text-xs text-muted-foreground">{data.reconciliation_memory_orphans.note}</p>
              <MemoryOrphanSamplesTable samples={data.reconciliation_memory_orphans.samples} />
            </section>

            {data.last_apply_sync_log && (
              <section className="space-y-1 text-xs text-muted-foreground border-t pt-3">
                <p className="font-medium text-foreground">Last apply run (db.sync_logs)</p>
                <p>Started: {formatDate(data.last_apply_sync_log.started_at)}</p>
                <p className="font-mono">{JSON.stringify(data.last_apply_sync_log.result)}</p>
              </section>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
